import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import LinkIcon from "@mui/icons-material/Link";
import ArticleIcon from "@mui/icons-material/Article";
import FilterListIcon from "@mui/icons-material/FilterList";
import WarningIcon from "@mui/icons-material/Warning";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Avatar from "@mui/material/Avatar";
import {
  adicionarAnexoSolicitacao,
  atualizarEmergencialSolicitacao,
  atualizarObservacaoSolicitacao,
  atualizarSituacaoSolicitacao,
  baixarArquivoSolicitacao,
  baixarAnexoSolicitacao,
  baixarDocumentoTecnico,
  excluirSolicitacao,
  listarSolicitacoes,
  marcarTipologiaComoReprovada,
  obterSolicitacao,
} from "services/modules/solicitacoes";
import {
  IPagedResponse,
  ISolicitacaoTipologiaDetalhe,
  ISolicitacaoTipologiaDetalheAnexo,
  ISolicitacaoTipologiaDetalheItem,
  ISolicitacaoTipologiaListaItem,
  SolicitacaoTipologiaAcao,
  SolicitacaoTipologiaSituacao,
} from "shared/interfaces/solicitacao";
import { getMeusProjetistas } from "services/modules/projetistas";
import { IProjetista } from "shared/interfaces/projetista";
import { useAuth } from "shared/hooks/auth";
import { getMeusCadastrosN2 } from "services/modules/cadastrosN2";
import { useNavigation } from "contexts/notificationsContext";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { alpha, useTheme } from "@mui/material/styles";
import NavigationHeader from "components/NavigationHeader";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import { TitleTable } from "components/TitleTable";
import { debounce } from "lodash";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import { DrawerFiltroAvancado, type IFiltroAvancado } from "./components";

const decodeLegacyText = (value?: string | null): string => {
  if (!value) return "";
  
  let result = value;
  
  // Substituir padrões conhecidos de encoding corrompido em português
  // Caractere "�" (replacement character U+FFFD) indica encoding incorreto
  const replacements: Array<[RegExp | string, string]> = [
    // Padrões comuns com "�" (U+FFFD)
    [/A\uFFFD\uFFFDO/gi, "AÇÃO"],
    [/a\uFFFD\uFFFDo/gi, "ação"],
    [/\uFFFD\uFFFDO/gi, "ÇÃO"],
    [/\uFFFD\uFFFDo/gi, "ção"],
    [/\uFFFD/g, ""], // remover caracteres inválidos restantes
    // UTF-8 mal interpretado como Latin-1
    ["Ã§", "ç"],
    ["Ã£", "ã"],
    ["Ãµ", "õ"],
    ["Ã¡", "á"],
    ["Ã©", "é"],
    ["Ã­", "í"],
    ["Ã³", "ó"],
    ["Ãº", "ú"],
    ["Ã¢", "â"],
    ["Ãª", "ê"],
    ["Ã´", "ô"],
    ["Ã ", "à"],
    ["Ã¨", "è"],
    ["Ã¬", "ì"],
    ["Ã²", "ò"],
    ["Ã¹", "ù"],
    ["Ã±", "ñ"],
    ["Ã¼", "ü"],
  ];
  
  for (const [pattern, replacement] of replacements) {
    if (typeof pattern === "string") {
      result = result.split(pattern).join(replacement);
    } else {
      result = result.replace(pattern, replacement);
    }
  }
  
  // Se não tem caracteres especiais corrompidos, tentar decodificação padrão
  const hasHighCodePoint = Array.from(result).some(
    (char) => char.charCodeAt(0) > 255
  );
  if (hasHighCodePoint) {
    return result;
  }
  try {
    const encoded = Array.from(result)
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
      .join("");
    return decodeURIComponent(encoded);
  } catch {
    return result;
  }
};

const situacaoLabels: Record<SolicitacaoTipologiaSituacao, string> = {
  [SolicitacaoTipologiaSituacao.Validacao]: "Validação",
  [SolicitacaoTipologiaSituacao.Homologacao]: "Homologação",
  [SolicitacaoTipologiaSituacao.Homologada]: "Homologada",
  [SolicitacaoTipologiaSituacao.DisponivelMercado]: "Disponível Mercado",
  [SolicitacaoTipologiaSituacao.Congelada]: "Congelada",
};

// Label especial para "Excluída do Pacote" (não é um valor do enum)
const SITUACAO_EXCLUIDA_DO_PACOTE = "Excluída do Pacote";

const itemSituacaoLabels: Record<number, string> = {
  1: "Aguardando Validação",
  2: "Em Validação",
  3: "Reprovado",
  4: "Homologação Pendente",
  5: "Homologado",
  6: "Disponibilizado",
};

const formatoData = (data: string) => {
  if (!data) return "-";
  const parsed = new Date(data);
  if (Number.isNaN(parsed.getTime())) {
    return data;
  }
  return parsed.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatoDataHora = (data: string | null | undefined): string => {
  if (!data) return "-";
  
  // Garantir que a string seja interpretada como UTC
  let dataUtcString = data;
  if (!dataUtcString.endsWith('Z') && !dataUtcString.includes('+') && !dataUtcString.includes('-', 10)) {
    // Se não tem indicador de timezone, assumir UTC
    dataUtcString = dataUtcString.endsWith('Z') ? dataUtcString : dataUtcString + 'Z';
  }
  
  const dataUtc = new Date(dataUtcString);
  
  // Converter para horário de Brasília (UTC-3)
  return dataUtc.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
};

export const Solicitacoes = () => {
  const { openNotification } = useNavigation();
  const { user } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();

  const [projetistas, setProjetistas] = useState<IProjetista[]>([]);
  const [projetistasPermitidos, setProjetistasPermitidos] = useState<number[]>([]);
  const [configCarregada, setConfigCarregada] = useState(false);
  // Frente 3 do plano de 22/09/2026: falha ao ler a configuração não pode se disfarçar de
  // "nenhum registro encontrado" — foi o que escondeu o 403 desde 06/09.
  const [erroConfiguracao, setErroConfiguracao] = useState(false);

  const [todasSolicitacoes, setTodasSolicitacoes] = useState<ISolicitacaoTipologiaListaItem[]>([]);
  const [solicitacoes, setSolicitacoes] = useState<ISolicitacaoTipologiaListaItem[]>([]);
  const [paginacao, setPaginacao] = useState<{ page: number; limit: number; totalRecords: number }>({
    page: 1,
    limit: 10,
    totalRecords: 0,
  });
  
  // Filtros por coluna
  const refPacote = useRef<HTMLInputElement>();
  const refProjetista = useRef<HTMLInputElement>();
  const refDescricao = useRef<HTMLInputElement>();
  const refCemPro = useRef<HTMLInputElement>();
  const refCemMobi = useRef<HTMLInputElement>();
  const refVinculo = useRef<HTMLInputElement>();
  const refData = useRef<HTMLInputElement>();

  const [filterColumnsDebounced, setFilterColumnsDebounced] = useState<{
    column?: string;
    value?: string;
  }>();

  const debouncedFilterColumn = useCallback(
    debounce((column: string, value: string) => {
      setFilterColumnsDebounced({ column, value });
    }, TIME_DEBOUNCE),
    [setFilterColumnsDebounced]
  );

  const [isLoading, setIsLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detalhe, setDetalhe] = useState<ISolicitacaoTipologiaDetalhe | null>(null);
  const [isLoadingDetalhe, setIsLoadingDetalhe] = useState(false);
  const [observacao, setObservacao] = useState("");
  const [uploadingAnexo, setUploadingAnexo] = useState(false);
  const [processingSituacao, setProcessingSituacao] = useState<null | SolicitacaoTipologiaAcao>(null);
  const [processingEmergencial, setProcessingEmergencial] = useState(false);
  const [processingReprovacao, setProcessingReprovacao] = useState(false);
  const [processingLinha, setProcessingLinha] = useState<{
    id: number;
    acao: SolicitacaoTipologiaAcao;
  } | null>(null);
  const [mostrarApenasReprovadas, setMostrarApenasReprovadas] = useState(false);
  const [savingObservacao, setSavingObservacao] = useState(false);
  const [modalEmergencialOpen, setModalEmergencialOpen] = useState(false);
  const [acaoEmergencial, setAcaoEmergencial] = useState<boolean>(false);
  const [downloadingLinha, setDownloadingLinha] = useState<number | null>(null);
  const [deletingLinha, setDeletingLinha] = useState<number | null>(null);
  const [detalheTab, setDetalheTab] = useState<"resumo" | "tipologias" | "historico" | "anexos">("resumo");
  const [tipologiaTab, setTipologiaTab] = useState<"cemPro" | "cemMobi">("cemPro");
  const [vinculoSelecionado, setVinculoSelecionado] = useState<number | "">("");
  const [modalDelete, setModalDelete] = useState<{ isOpen: boolean; id?: number }>({ isOpen: false });

  // Estados para os novos modais/componentes
  const [drawerFiltroOpen, setDrawerFiltroOpen] = useState(false);
  const [filtrosAvancados, setFiltrosAvancados] = useState<IFiltroAvancado>({});

  // Como não temos empresa selecionada, vamos permitir ações e deixar o backend validar
  // O modoUtilizacao será verificado no backend baseado na empresa de cada solicitação
  const modoValidacao = true; // Permitir validação - backend valida se realmente pode
  const modoHomologacao = true; // Permitir homologação - backend valida se realmente pode

  const carregarProjetistas = async () => {
    try {
      const meusProjetistas = await getMeusProjetistas();
      setProjetistas(meusProjetistas.filter((p) => p.ativo));
    } catch (error: any) {
      console.error(
        "[Solicitacoes] Erro ao carregar os projetistas da empresa (status " +
          (error?.response?.status ?? error?.status ?? "?") +
          "):",
        error
      );
      setProjetistas([]);
    }
  };

  const carregarSolicitacoes = useCallback(async () => {
    if (erroConfiguracao) {
      setTodasSolicitacoes([]);
      setIsLoading(false);
      return;
    }

    if (projetistasPermitidos.length === 0 && configCarregada) {
      setTodasSolicitacoes([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const listaSolicitacoes: ISolicitacaoTipologiaListaItem[] = [];
      let page = 1;
      const limit = 200;
      let hasMore = true;

      const parametros: Record<string, any> = {
        page,
        limit,
      };

      if (projetistasPermitidos.length > 0) {
        parametros.projetistaIds = projetistasPermitidos.join(",");
      }

      if (filtrosAvancados.projetistaId) {
        parametros.projetistaId = filtrosAvancados.projetistaId;
      }

      while (hasMore) {
        const resposta = await listarSolicitacoes({
          ...parametros,
          page,
          limit,
        });

        const paged = resposta as IPagedResponse<ISolicitacaoTipologiaListaItem>;
        const items = paged.items ?? [];
        
        listaSolicitacoes.push(...items);

        // Verificar se há mais páginas
        hasMore = items.length === limit && (paged.totalRecords ?? 0) > listaSolicitacoes.length;
        page++;
      }

      // Ordenar por data mais recente primeiro
      listaSolicitacoes.sort((a, b) => {
        const dataA = new Date(a.dataSolicitacao).getTime();
        const dataB = new Date(b.dataSolicitacao).getTime();
        return dataB - dataA;
      });

      // Armazenar todas as solicitações
      setTodasSolicitacoes(listaSolicitacoes);
    } catch (error) {
      openNotification({
        message: "Erro ao carregar solicitações.",
        type: "error",
      });
      setTodasSolicitacoes([]);
    } finally {
      setIsLoading(false);
    }
  }, [filtrosAvancados, projetistasPermitidos, configCarregada, erroConfiguracao, openNotification]);

  const carregarDetalhe = async (idSolicitacao: number) => {
    setIsLoadingDetalhe(true);
    const detalheResponse = await obterSolicitacao(idSolicitacao);
    if (detalheResponse) {
      setDetalhe(detalheResponse);
      setObservacao(detalheResponse.observacaoInterna ?? "");
      setDetalheTab("resumo");
    }
    setIsLoadingDetalhe(false);
  };

  const abrirDetalhe = (idSolicitacao: number) => {
    setDrawerOpen(true);
    carregarDetalhe(idSolicitacao);
  };

  useEffect(() => {
    carregarProjetistas();
  }, []);

  useEffect(() => {
    const carregarConfiguracao = async () => {
      if (user) {
        try {
          // Endpoint por token: a empresa vem das claims. O endpoint por id exige a tela
          // Configuracao (Gerência) e devolvia 403 ao usuário de Cadastros N2 — a tela ficava
          // vazia sem dizer por quê (plano de 22/09/2026, Frentes 1 e 3).
          const cadastrosN2 = await getMeusCadastrosN2({ propagarErro: true });
          setErroConfiguracao(false);
          if (cadastrosN2?.projetistasSolicitacoes?.length) {
            setProjetistasPermitidos(cadastrosN2.projetistasSolicitacoes);
          } else {
            setProjetistasPermitidos([]);
          }
        } catch (error: any) {
          console.error(
            "[Solicitacoes] Erro ao carregar as configurações de Cadastros N2 (status " +
              (error?.response?.status ?? error?.status ?? "?") +
              "):",
            error
          );
          setErroConfiguracao(true);
          setProjetistasPermitidos([]);
        }
      }
      setConfigCarregada(true);
    };
    carregarConfiguracao();
    // Recarrega na troca de usuário; a empresa não entra mais na dependência porque vem do token.
  }, [user?.idUsuario, user?.idEmpresa]);

  // Recarregar solicitações quando filtros avançados mudarem ou config carregar
  useEffect(() => {
    if (configCarregada) {
      carregarSolicitacoes();
    }
  }, [carregarSolicitacoes, configCarregada]);

  // Aplicar filtros e paginação localmente quando mudar
  useEffect(() => {
    let solicitacoesFiltradas = todasSolicitacoes;

    // Aplicar filtros avançados locais (linhaNome e descricaoBusca)
    if (filtrosAvancados.linhaNome) {
      solicitacoesFiltradas = solicitacoesFiltradas.filter((s) => 
        decodeLegacyText(s.linhaSistema) === filtrosAvancados.linhaNome
      );
    }
    
    if (filtrosAvancados.descricaoBusca) {
      const buscaLower = filtrosAvancados.descricaoBusca.toLowerCase();
      solicitacoesFiltradas = solicitacoesFiltradas.filter((s) => 
        decodeLegacyText(s.descricao)?.toLowerCase().includes(buscaLower)
      );
    }

    // Aplicar filtro de coluna se houver
    if (filterColumnsDebounced?.value) {
      const filtro = filterColumnsDebounced.value.toLowerCase();
      const coluna = filterColumnsDebounced.column;

      solicitacoesFiltradas = solicitacoesFiltradas.filter((solicitacao) => {
        switch (coluna) {
          case "pacote":
            return String(solicitacao.numeroSolicitacao).padStart(7, "0").includes(filtro);
          case "projetista":
            return solicitacao.projetistaNome.toLowerCase().includes(filtro);
          case "descricao":
            return decodeLegacyText(solicitacao.descricao)?.toLowerCase().includes(filtro) || false;
          case "cemPro":
          case "cemMobi":
            const situacao = situacaoLabels[solicitacao.situacao] || "";
            return situacao.toLowerCase().includes(filtro);
          case "vinculo":
            return solicitacao.vinculada ? "vinculada".includes(filtro) : "não vinculada".includes(filtro);
          case "data":
            return formatoData(solicitacao.dataSolicitacao).includes(filtro);
          default:
            return (
              String(solicitacao.numeroSolicitacao).padStart(7, "0").includes(filtro) ||
              solicitacao.projetistaNome.toLowerCase().includes(filtro) ||
              decodeLegacyText(solicitacao.descricao)?.toLowerCase().includes(filtro) ||
              situacaoLabels[solicitacao.situacao]?.toLowerCase().includes(filtro)
            );
        }
      });
    }

    // Aplicar paginação
    const inicio = (paginacao.page - 1) * paginacao.limit;
    const fim = inicio + paginacao.limit;
    const solicitacoesPaginadas = solicitacoesFiltradas.slice(inicio, fim);

    setSolicitacoes(solicitacoesPaginadas);
    setPaginacao((prev) => ({
      ...prev,
      totalRecords: solicitacoesFiltradas.length,
    }));
  }, [todasSolicitacoes, paginacao.page, paginacao.limit, filterColumnsDebounced, filtrosAvancados]);



  const situacaoChipColorMap: Record<
    SolicitacaoTipologiaSituacao,
    "default" | "primary" | "secondary" | "error" | "warning" | "info" | "success"
  > = {
    [SolicitacaoTipologiaSituacao.Validacao]: "warning",
    [SolicitacaoTipologiaSituacao.Homologacao]: "info",
    [SolicitacaoTipologiaSituacao.Homologada]: "success",
    [SolicitacaoTipologiaSituacao.DisponivelMercado]: "primary",
    [SolicitacaoTipologiaSituacao.Congelada]: "default",
  };

  const columns: GridColDef[] = [
    {
      field: "numeroSolicitacao",
      headerName: "Pacote N°",
      width: 150,
      renderHeader: () => (
        <FilterColumn
          inputRef={refPacote}
          label="Pacote N°"
          name="pacote"
          onChange={(event) => {
            if (refProjetista.current) refProjetista.current.value = "";
            if (refDescricao.current) refDescricao.current.value = "";
            if (refCemPro.current) refCemPro.current.value = "";
            if (refCemMobi.current) refCemMobi.current.value = "";
            if (refVinculo.current) refVinculo.current.value = "";
            if (refData.current) refData.current.value = "";
            debouncedFilterColumn("pacote", event.target.value);
          }}
        />
      ),
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2" fontWeight={600}>
            {String(params.row.numeroSolicitacao).padStart(7, "0")}
          </Typography>
          {params.row.emergencial && (
            <Tooltip title="Emergencial">
              <WarningIcon sx={{ color: "#ff9800", fontSize: "1.2rem" }} />
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      field: "projetistaNome",
      headerName: "Projetista(s)",
      flex: 1.5,
      minWidth: 200,
      renderHeader: () => (
        <FilterColumn
          inputRef={refProjetista}
          label="Projetista(s)"
          name="projetista"
          onChange={(event) => {
            if (refPacote.current) refPacote.current.value = "";
            if (refDescricao.current) refDescricao.current.value = "";
            if (refCemPro.current) refCemPro.current.value = "";
            if (refCemMobi.current) refCemMobi.current.value = "";
            if (refVinculo.current) refVinculo.current.value = "";
            if (refData.current) refData.current.value = "";
            debouncedFilterColumn("projetista", event.target.value);
          }}
        />
      ),
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          {params.row.projetistaNome}
        </Typography>
      ),
    },
    {
      field: "descricao",
      headerName: "Descrição",
      flex: 2,
      minWidth: 250,
      renderHeader: () => (
        <FilterColumn
          inputRef={refDescricao}
          label="Descrição"
          name="descricao"
          onChange={(event) => {
            if (refPacote.current) refPacote.current.value = "";
            if (refProjetista.current) refProjetista.current.value = "";
            if (refCemPro.current) refCemPro.current.value = "";
            if (refCemMobi.current) refCemMobi.current.value = "";
            if (refVinculo.current) refVinculo.current.value = "";
            if (refData.current) refData.current.value = "";
            debouncedFilterColumn("descricao", event.target.value);
          }}
        />
      ),
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            lineHeight: 1.4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
          title={decodeLegacyText(params.row.descricao) || "Não informado"}
        >
          {decodeLegacyText(params.row.descricao) || "Não informado"}
        </Typography>
      ),
    },
    {
      field: "cemPro",
      headerName: "CEM Desktop",
      width: 150,
      renderHeader: () => (
        <FilterColumn
          inputRef={refCemPro}
          label="CEM Desktop"
          name="cemPro"
          onChange={(event) => {
            if (refPacote.current) refPacote.current.value = "";
            if (refProjetista.current) refProjetista.current.value = "";
            if (refDescricao.current) refDescricao.current.value = "";
            if (refCemMobi.current) refCemMobi.current.value = "";
            if (refVinculo.current) refVinculo.current.value = "";
            if (refData.current) refData.current.value = "";
            debouncedFilterColumn("cemPro", event.target.value);
          }}
        />
      ),
      renderCell: (params) => {
        const row = params.row as ISolicitacaoTipologiaListaItem;
        const situacaoValue = row.situacaoCemDelphi as SolicitacaoTipologiaSituacao;
        
        if (row.todasTipologiasExcluidasCemDelphi) {
          return (
            <Chip
              label={SITUACAO_EXCLUIDA_DO_PACOTE}
              color="default"
              size="small"
              sx={{ fontWeight: 600, width: "fit-content" }}
            />
          );
        }

        const situacao = situacaoLabels[situacaoValue] || "";
        
        const dataDisponibilizacao = row.dataDisponibilizacaoCemDelphi;
        const mostrarTooltip = situacaoValue === SolicitacaoTipologiaSituacao.DisponivelMercado && dataDisponibilizacao;
        
        const chip = (
          <Chip
            label={situacao}
            color={situacaoChipColorMap[situacaoValue]}
            size="small"
            sx={{ fontWeight: 600, width: "fit-content" }}
          />
        );
        
        if (mostrarTooltip) {
          return (
            <Tooltip title={`Data disponibilização mercado: ${formatoDataHora(dataDisponibilizacao)}`} arrow>
              {chip}
            </Tooltip>
          );
        }
        
        return chip;
      },
    },
    {
      field: "cemMobi",
      headerName: "CEM Mobi",
      width: 150,
      renderHeader: () => (
        <FilterColumn
          inputRef={refCemMobi}
          label="CEM Mobi"
          name="cemMobi"
          onChange={(event) => {
            if (refPacote.current) refPacote.current.value = "";
            if (refProjetista.current) refProjetista.current.value = "";
            if (refDescricao.current) refDescricao.current.value = "";
            if (refCemPro.current) refCemPro.current.value = "";
            if (refVinculo.current) refVinculo.current.value = "";
            if (refData.current) refData.current.value = "";
            debouncedFilterColumn("cemMobi", event.target.value);
          }}
        />
      ),
      renderCell: (params) => {
        const row = params.row as ISolicitacaoTipologiaListaItem;
        const situacaoValue = row.situacaoCemMobi as SolicitacaoTipologiaSituacao;
        
        if (row.todasTipologiasExcluidasCemMobi) {
          return (
            <Chip
              label={SITUACAO_EXCLUIDA_DO_PACOTE}
              color="default"
              size="small"
              sx={{ fontWeight: 600, width: "fit-content" }}
            />
          );
        }

        const situacao = situacaoLabels[situacaoValue] || "";
        
        const dataDisponibilizacao = row.dataDisponibilizacaoCemMobi;
        const mostrarTooltip = situacaoValue === SolicitacaoTipologiaSituacao.DisponivelMercado && dataDisponibilizacao;
        
        const chip = (
          <Chip
            label={situacao}
            color={situacaoChipColorMap[situacaoValue]}
            size="small"
            sx={{ fontWeight: 600, width: "fit-content" }}
          />
        );
        
        if (mostrarTooltip) {
          return (
            <Tooltip title={`Data disponibilização mercado: ${formatoDataHora(dataDisponibilizacao)}`} arrow>
              {chip}
            </Tooltip>
          );
        }
        
        return chip;
      },
    },
    {
      field: "vinculo",
      headerName: "Vínculo",
      width: 120,
      renderHeader: () => (
        <FilterColumn
          inputRef={refVinculo}
          label="Vínculo"
          name="vinculo"
          onChange={(event) => {
            if (refPacote.current) refPacote.current.value = "";
            if (refProjetista.current) refProjetista.current.value = "";
            if (refDescricao.current) refDescricao.current.value = "";
            if (refCemPro.current) refCemPro.current.value = "";
            if (refCemMobi.current) refCemMobi.current.value = "";
            if (refData.current) refData.current.value = "";
            debouncedFilterColumn("vinculo", event.target.value);
          }}
        />
      ),
      renderCell: (params) => (
        params.row.vinculada ? (
          <Tooltip title="Solicitação vinculada a outras solicitações">
            <LinkIcon color="primary" sx={{ fontSize: 20 }} />
          </Tooltip>
        ) : null
      ),
    },
    {
      field: "dataSolicitacao",
      headerName: "Data",
      width: 120,
      renderHeader: () => (
        <FilterColumn
          inputRef={refData}
          label="Data"
          name="data"
          onChange={(event) => {
            if (refPacote.current) refPacote.current.value = "";
            if (refProjetista.current) refProjetista.current.value = "";
            if (refDescricao.current) refDescricao.current.value = "";
            if (refCemPro.current) refCemPro.current.value = "";
            if (refCemMobi.current) refCemMobi.current.value = "";
            if (refVinculo.current) refVinculo.current.value = "";
            debouncedFilterColumn("data", event.target.value);
          }}
        />
      ),
      renderCell: (params) => (
        <Typography variant="body2">{formatoData(params.row.dataSolicitacao)}</Typography>
      ),
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row as ISolicitacaoTipologiaListaItem;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {/* Documento Técnico (PDF do pacote CEM) */}
            <Tooltip
              title={!row.documentoPdfCaminho ? "Processando documento..." : "Baixar Documento Técnico"}
              placement="top"
            >
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={downloadingLinha === row.idSolicitacao || !row.documentoPdfCaminho}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDownloadDocumentoTecnico(row.idSolicitacao);
                  }}
                >
                  <ArticleIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            {/* Visualizar tipologias (modal grande) */}
            <Tooltip title="Ver Tipologias">
              <IconButton
                size="small"
                color="primary"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/solicitacoesMof/${row.idSolicitacao}`);
                }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Excluir - apenas se ambos os sistemas NÃO estiverem "Disponível para Mercado", não congelada e com permissão */}
            {modoValidacao && 
             !row.congelada && 
             row.situacaoCemDelphi !== SolicitacaoTipologiaSituacao.DisponivelMercado &&
             row.situacaoCemMobi !== SolicitacaoTipologiaSituacao.DisponivelMercado && (
              <Tooltip title="Excluir Solicitação">
                <IconButton
                  size="small"
                  color="error"
                  disabled={deletingLinha === row.idSolicitacao}
                  onClick={(event) => {
                    event.stopPropagation();
                    setModalDelete({ isOpen: true, id: row.idSolicitacao });
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  const handlePaginaChange = (page: number) => {
    setPaginacao((prev) => ({ ...prev, page: page + 1 }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPaginacao({ page: 1, limit: pageSize, totalRecords: paginacao.totalRecords });
  };

  const podeValidar = useMemo(() => {
    if (!detalhe) return false;
    if (detalhe.congelada) return false;
    return modoValidacao && detalhe.situacao === SolicitacaoTipologiaSituacao.Validacao;
  }, [detalhe, modoValidacao]);

  const podeHomologar = useMemo(() => {
    if (!detalhe) return false;
    if (detalhe.congelada) return false;
    return modoHomologacao && detalhe.situacao === SolicitacaoTipologiaSituacao.Homologacao;
  }, [detalhe, modoHomologacao]);

  const podeDisponibilizar = useMemo(() => {
    if (!detalhe) return false;
    if (detalhe.congelada) return false;
    return detalhe.situacao === SolicitacaoTipologiaSituacao.Homologada;
  }, [detalhe]);

  const temDescontinuados = useMemo(() => {
    if (!detalhe) return false;
    return detalhe.tipologias.some((t) => t.descontinuado && t.situacao !== 3);
  }, [detalhe]);

  const tipologiasFiltradas = useMemo(() => {
    if (!detalhe) return [];
    if (mostrarApenasReprovadas) {
      // Filtrar tipologias excluídas do pacote (substituídas) ou reprovadas
      return detalhe.tipologias.filter((t) => t.substituida || t.situacao === 3);
    }
    return detalhe.tipologias;
  }, [detalhe, mostrarApenasReprovadas]);

  const estaProcessandoAcao = (solicitacaoId: number, acao: SolicitacaoTipologiaAcao) =>
    processingLinha?.id === solicitacaoId && processingLinha?.acao === acao;

  const executarAcaoSituacao = async (acao: SolicitacaoTipologiaAcao) => {
    if (!detalhe) return;
    setProcessingSituacao(acao);
    const sucesso = await atualizarSituacaoSolicitacao(detalhe.idSolicitacao, acao);
    setProcessingSituacao(null);
    if (sucesso) {
      openNotification({
        message: "Solicitação atualizada com sucesso.",
        type: "success",
      });
      await carregarDetalhe(detalhe.idSolicitacao);
      await carregarSolicitacoes();
    }
  };

  const mensagensAcao: Record<SolicitacaoTipologiaAcao, string> = {
    [SolicitacaoTipologiaAcao.Validar]: "Solicitação validada com sucesso.",
    [SolicitacaoTipologiaAcao.ConcluirHomologacao]: "Homologação concluída com sucesso.",
    [SolicitacaoTipologiaAcao.DisponibilizarMercado]: "Solicitação disponibilizada ao mercado.",
  };

  const executarAcaoLinha = async (
    solicitacao: ISolicitacaoTipologiaListaItem,
    acao: SolicitacaoTipologiaAcao
  ) => {
    setProcessingLinha({ id: solicitacao.idSolicitacao, acao });
    const sucesso = await atualizarSituacaoSolicitacao(solicitacao.idSolicitacao, acao);
    setProcessingLinha(null);

    if (sucesso) {
      openNotification({
        message: mensagensAcao[acao],
        type: "success",
      });
      await carregarSolicitacoes();
      if (detalhe?.idSolicitacao === solicitacao.idSolicitacao) {
        await carregarDetalhe(solicitacao.idSolicitacao);
      }
    }
  };

  const salvarObservacao = async () => {
    if (!detalhe) return;
    setSavingObservacao(true);
    const sucesso = await atualizarObservacaoSolicitacao(detalhe.idSolicitacao, observacao);
    setSavingObservacao(false);
    if (sucesso) {
      openNotification({
        message: "Observação salva com sucesso.",
        type: "success",
      });
      await carregarDetalhe(detalhe.idSolicitacao);
    }
  };

  const solicitarConfirmacaoEmergencial = (checked: boolean) => {
    if (!detalhe) return;
    setAcaoEmergencial(checked);
    setModalEmergencialOpen(true);
  };

  const confirmarEmergencial = async () => {
    await atualizarEmergencial(acaoEmergencial);
  };

  const atualizarEmergencial = async (checked: boolean) => {
    if (!detalhe) return;
    setProcessingEmergencial(true);
    const sucesso = await atualizarEmergencialSolicitacao(detalhe.idSolicitacao, checked);
    setProcessingEmergencial(false);
    if (sucesso) {
      setModalEmergencialOpen(false);
      openNotification({
        message: checked
          ? "Solicitação marcada como emergencial. Outras solicitações do projetista foram congeladas."
          : "Emergencial removida. Solicitações do projetista foram liberadas.",
        type: "success",
      });
      await carregarDetalhe(detalhe.idSolicitacao);
      await carregarSolicitacoes();
    }
  };

  const handleUploadAnexo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!detalhe) return;
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingAnexo(true);
    const anexo = await adicionarAnexoSolicitacao(detalhe.idSolicitacao, file);
    setUploadingAnexo(false);
    event.target.value = "";
    if (anexo) {
      openNotification({
        message: "Anexo adicionado com sucesso.",
        type: "success",
      });
      await carregarDetalhe(detalhe.idSolicitacao);
    }
  };

  const handleReprovarTipologia = async (itemId: number) => {
    if (!detalhe) return;
    setProcessingReprovacao(true);
    const sucesso = await marcarTipologiaComoReprovada(detalhe.idSolicitacao, itemId);
    setProcessingReprovacao(false);
    if (sucesso) {
      openNotification({
        message: "Tipologia marcada como reprovada.",
        type: "success",
      });
      await carregarDetalhe(detalhe.idSolicitacao);
    }
  };

  const handleDownloadDocumentoTecnico = async (solicitacaoId: number) => {
    setDownloadingLinha(solicitacaoId);
    const blob = await baixarDocumentoTecnico(solicitacaoId);
    setDownloadingLinha(null);
    if (!blob) {
      openNotification({
        message: "Documento técnico (PDF) não disponível para esta solicitação. O PDF só está disponível para solicitações importadas após a atualização do sistema.",
        type: "warning",
      });
      return;
    }
    
    // Extrair nome do arquivo do blob (se disponível) ou usar nome genérico
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    // O nome do arquivo será definido pelo Content-Disposition do backend
    link.setAttribute("download", "");
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExcluirLinha = async (solicitacaoId: number) => {
    setDeletingLinha(solicitacaoId);
    try {
      const sucesso = await excluirSolicitacao(solicitacaoId);
      setDeletingLinha(null);

      if (sucesso) {
        openNotification({
          message: "Solicitação excluída com sucesso.",
          type: "success",
        });
        await carregarSolicitacoes();
      }
    } catch (error: any) {
      setDeletingLinha(null);
      // Captura o erro e mostra uma notificação amigável ao invés de lançar erro não tratado
      const errorMessage = error?.message || "Erro ao excluir solicitação. Verifique se a solicitação pode ser excluída.";
      openNotification({
        message: errorMessage,
        type: "error",
      });
    } finally {
      setModalDelete({ isOpen: false });
    }
  };

  const downloadArquivoOriginal = async () => {
    if (!detalhe) return;
    const blob = await baixarArquivoSolicitacao(detalhe.idSolicitacao);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", detalhe.nomeArquivo);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadAnexo = async (anexo: ISolicitacaoTipologiaDetalheAnexo) => {
    if (!detalhe) return;
    const blob = await baixarAnexoSolicitacao(detalhe.idSolicitacao, anexo.idAnexo);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", anexo.nomeArquivo);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleAplicarFiltrosAvancados = (novosFiltros: IFiltroAvancado) => {
    setFiltrosAvancados(novosFiltros);
    // O useEffect já irá recarregar as solicitações quando filtrosAvancados mudar
    // Resetar paginação para primeira página
    setPaginacao((prev) => ({ ...prev, page: 1 }));
  };

  // Verifica se há filtros ativos
  const temFiltrosAtivos = useMemo(() => {
    return (
      filtrosAvancados.projetistaId !== undefined ||
      filtrosAvancados.linhaNome !== undefined ||
      filtrosAvancados.descricaoBusca !== undefined
    );
  }, [filtrosAvancados]);

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title="SOLICITAÇÕES"
        label="Solicitações"
        hasBackButton={false}
      />
      <Grid container display="flex" alignItems="center">
        <StackCustom>
          <Grid container mb={4} justifyContent="space-between" alignItems="center">
            <Grid item xs={12} sm={6} md={6}>
              <TitleTable title="Solicitações" />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant={temFiltrosAtivos ? "contained" : "outlined"}
                  color={temFiltrosAtivos ? "primary" : "inherit"}
                  startIcon={<FilterListIcon />}
                  onClick={() => setDrawerFiltroOpen(true)}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Filtro avançado
                  {temFiltrosAtivos && (
                    <Chip
                      label="Ativo"
                      size="small"
                      color="secondary"
                      sx={{ ml: 1, height: 20, fontSize: "0.65rem" }}
                    />
                  )}
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {/* Frente 3 do plano de 22/09/2026: sem isto, falha de leitura da configuração e
              ausência real de solicitações apareciam as duas como "nenhum registro encontrado". */}
          {erroConfiguracao && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Não foi possível carregar as configurações de Cadastros N2 desta empresa, por isso a
              lista não pôde ser montada. Atualize a página e, se continuar, avise o suporte.
            </Alert>
          )}

          {/* Tabela */}
          <Paper>
            <StripedDataGrid
              autoHeight
              rows={solicitacoes}
              columns={columns}
              loading={isLoading}
              page={paginacao.page - 1}
              pageSize={paginacao.limit}
              rowsPerPageOptions={[10, 20, 50]}
              paginationMode="client"
              rowCount={paginacao.totalRecords}
              checkboxSelection={false}
              hideFooter={false}
              onPageChange={(page) => {
                handlePaginaChange(page);
              }}
              onPageSizeChange={(pageSize) => {
                handlePageSizeChange(pageSize);
              }}
              getRowId={(row) => row.idSolicitacao}
              sx={{
                mt: 1,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                  color: theme.palette.primary.dark,
                  fontWeight: 600,
                  letterSpacing: 0.2,
                },
                "& .MuiDataGrid-cell": {
                  py: 1.5,
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            />
          </Paper>
        </StackCustom>
      </Grid>

      {/* Drawer de Detalhes */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: { xs: "100%", sm: 600 }, maxWidth: "100vw", p: 3, overflowY: "auto" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight={700} color="primary">
              Detalhes da Solicitação
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          {isLoadingDetalhe && <Typography>Carregando...</Typography>}

          {!isLoadingDetalhe && detalhe && (
            <Box>
              {/* Cabeçalho da Solicitação */}
              <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: "background.paper" }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                      Nº SOLICITAÇÃO
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
                      {String(detalhe.numeroSolicitacao).padStart(7, "0")}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                      PROJETISTA
                    </Typography>
                    <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
                      {detalhe.projetistaNome}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                    <Chip
                      label={situacaoLabels[detalhe.situacao]}
                      color="primary"
                      size="medium"
                      sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                    />
                    {detalhe.emergencial && (
                      <Chip
                        label="EMERGENCIAL"
                        color="error"
                        size="medium"
                        sx={{ fontWeight: 700, fontSize: "0.875rem" }}
                      />
                    )}
                    {detalhe.congelada && (
                      <Chip
                        label="CONGELADA"
                        color="warning"
                        size="medium"
                        sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                      />
                    )}
                    {temDescontinuados && (
                      <Chip
                        label="⚠️ DESCONTINUADOS"
                        color="warning"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                </Stack>
              </Paper>

              {detalhe.emergencial && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Esta solicitação está marcada como atualização emergencial. Solicitações do mesmo
                  projetista estão temporariamente congeladas.
                </Alert>
              )}

              {detalhe.congelada && !detalhe.emergencial && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Há uma atualização emergencial em andamento para este projetista. Esta solicitação
                  permanecerá apenas para consulta até a conclusão da prioridade.
                </Alert>
              )}

              {temDescontinuados && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: "error.lighter",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "error.main",
                  }}
                >
                  <Typography variant="body2" color="error.dark">
                    ⚠️ Esta solicitação contém tipologias com elementos descontinuados. Corrija antes de
                    continuar.
                  </Typography>
                </Box>
              )}

              <Tabs
                value={detalheTab}
                onChange={(_, value) => setDetalheTab(value)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{
                  borderBottom: 2,
                  borderColor: "divider",
                  mb: 3,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    minHeight: 48,
                  },
                }}
              >
                <Tab label="Resumo" value="resumo" />
                <Tab label={`Tipologias (${detalhe.tipologias.length})`} value="tipologias" />
                <Tab label={`Histórico (${detalhe.historicos.length})`} value="historico" />
                <Tab label={`Anexos (${detalhe.anexos.length})`} value="anexos" />
              </Tabs>

              {detalheTab === "resumo" && (
                <Stack spacing={3}>
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 3, color: "primary.main" }}>
                      Informações da Solicitação
                    </Typography>

                    {/* Vínculos */}
                    {(detalhe.vinculos.length > 1 || detalhe.numeroSolicitacaoAnterior) && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 1, display: "block" }}>
                          VÍNCULOS
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip
                            size="small"
                            color="primary"
                            label={`Origem #${String(detalhe.numeroSolicitacaoOrigem).padStart(7, "0")}`}
                            sx={{ fontWeight: 600 }}
                          />
                          {detalhe.numeroSolicitacaoAnterior && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Anterior #${String(detalhe.numeroSolicitacaoAnterior).padStart(7, "0")}`}
                            />
                          )}
                          {detalhe.vinculos.length > 1 &&
                            detalhe.vinculos.map((vinculo) => (
                              <Chip
                                key={vinculo.idSolicitacao}
                                label={`#${String(vinculo.numeroSolicitacao).padStart(7, "0")} · ${
                                  situacaoLabels[vinculo.situacao]
                                }`}
                                color={vinculo.atual ? "primary" : "default"}
                                variant={vinculo.atual ? "filled" : "outlined"}
                                size="small"
                                onClick={() => {
                                  if (!vinculo.atual) {
                                    abrirDetalhe(vinculo.idSolicitacao);
                                  }
                                }}
                                sx={{
                                  fontWeight: vinculo.atual ? 700 : 500,
                                  cursor: vinculo.atual ? "default" : "pointer",
                                }}
                              />
                            ))}
                        </Stack>
                      </Box>
                    )}

                    {/* Informações principais */}
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                          NÚMERO DA SOLICITAÇÃO
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {String(detalhe.numeroSolicitacao).padStart(7, "0")}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                          DESCRIÇÃO DA SOLICITAÇÃO
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {detalhe.descricaoAtualizacao || "Não informado"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                          CEM Desktop
                        </Typography>
                        <Chip
                          label={situacaoLabels[detalhe.situacaoCemDelphi]}
                          color={situacaoChipColorMap[detalhe.situacaoCemDelphi]}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                          CEM MOBI
                        </Typography>
                        <Chip
                          label={situacaoLabels[detalhe.situacaoCemMobi]}
                          color={situacaoChipColorMap[detalhe.situacaoCemMobi]}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                          LINHA / SISTEMA
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {detalhe.linhaSistema || "Não informado"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                          DATA DE ENVIO
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {formatoData(detalhe.dataEnvio)}
                        </Typography>
                      </Grid>
                      {detalhe.emergencial && detalhe.dataEmergencial && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                              DATA DA MARCAÇÃO EMERGENCIAL
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {formatoData(detalhe.dataEmergencial)}
                            </Typography>
                          </Grid>
                          {detalhe.responsavelEmergencial && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                                RESPONSÁVEL PELA MARCAÇÃO
                              </Typography>
                              <Typography variant="body1" fontWeight={500}>
                                {detalhe.responsavelEmergencial}
                              </Typography>
                            </Grid>
                          )}
                        </>
                      )}
                      {detalhe.dataLiberacaoEmergencial && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                              DATA DE LIBERAÇÃO DA EMERGENCIAL
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {formatoData(detalhe.dataLiberacaoEmergencial)}
                            </Typography>
                          </Grid>
                          {detalhe.responsavelLiberacaoEmergencial && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                                RESPONSÁVEL PELA LIBERAÇÃO
                              </Typography>
                              <Typography variant="body1" fontWeight={500}>
                                {detalhe.responsavelLiberacaoEmergencial}
                              </Typography>
                            </Grid>
                          )}
                        </>
                      )}
                    </Grid>

                    {/* Divisor */}
                    <Box sx={{ my: 3, borderTop: 1, borderColor: "divider" }} />

                    {/* Ações principais */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: "text.secondary" }}>
                        AÇÕES DO FLUXO
                      </Typography>
                      <Stack direction="column" spacing={2}>
                        {/* Primeira linha de botões */}
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                          {podeValidar && (
                          <LoadingButton
                            variant="contained"
                            color="primary"
                            size="medium"
                            loading={processingSituacao === SolicitacaoTipologiaAcao.Validar}
                            onClick={() => executarAcaoSituacao(SolicitacaoTipologiaAcao.Validar)}
                            sx={{ flex: { xs: "1 1 100%", sm: "0 1 auto" }, minWidth: 140, textTransform: "none", fontWeight: 600 }}
                          >
                            Validar
                          </LoadingButton>
                          )}
                          {podeHomologar && (
                          <LoadingButton
                            variant="contained"
                            color="info"
                            size="medium"
                            loading={processingSituacao === SolicitacaoTipologiaAcao.ConcluirHomologacao}
                            onClick={() => executarAcaoSituacao(SolicitacaoTipologiaAcao.ConcluirHomologacao)}
                            sx={{ flex: { xs: "1 1 100%", sm: "0 1 auto" }, minWidth: 180, textTransform: "none", fontWeight: 600 }}
                          >
                            Concluir Homologação
                          </LoadingButton>
                          )}
                          {podeDisponibilizar && (
                          <LoadingButton
                            variant="contained"
                            color="success"
                            size="medium"
                            loading={processingSituacao === SolicitacaoTipologiaAcao.DisponibilizarMercado}
                            onClick={() =>
                              executarAcaoSituacao(SolicitacaoTipologiaAcao.DisponibilizarMercado)
                            }
                            sx={{ flex: { xs: "1 1 100%", sm: "0 1 auto" }, minWidth: 180, textTransform: "none", fontWeight: 600 }}
                          >
                            Disponibilizar Mercado
                          </LoadingButton>
                          )}
                        </Stack>
                        {/* Segunda linha de botões */}
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                          <LoadingButton
                            variant={detalhe.emergencial ? "contained" : "outlined"}
                            color={detalhe.emergencial ? "error" : "warning"}
                            size="medium"
                            loading={processingEmergencial}
                            disabled={processingEmergencial || detalhe.congelada}
                            onClick={() => solicitarConfirmacaoEmergencial(!detalhe.emergencial)}
                            sx={{ flex: { xs: "1 1 100%", sm: "0 1 auto" }, minWidth: 180, textTransform: "none", fontWeight: 600 }}
                          >
                            {detalhe.emergencial ? "Remover Emergencial" : "Marcar Emergencial"}
                          </LoadingButton>
                          <Button
                            variant="outlined"
                            size="medium"
                            startIcon={<DownloadIcon />}
                            onClick={downloadArquivoOriginal}
                            sx={{ flex: { xs: "1 1 100%", sm: "0 1 auto" }, minWidth: 180, textTransform: "none", fontWeight: 600 }}
                          >
                            Baixar Arquivo Original
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: "primary.main" }}>
                      Observações Internas
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={5}
                      maxRows={10}
                      value={observacao}
                      onChange={(event) => setObservacao(event.target.value)}
                      placeholder="Registre observações internas sobre esta solicitação."
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2.5 }}>
                      <LoadingButton
                        variant="contained"
                        size="medium"
                        loading={savingObservacao}
                        onClick={salvarObservacao}
                        sx={{ minWidth: 160, textTransform: "none", fontWeight: 600 }}
                      >
                        Salvar Observação
                      </LoadingButton>
                    </Stack>
                  </Paper>
                </Stack>
              )}

              {detalheTab === "tipologias" && (
                <Stack spacing={2.5}>
                  {/* Informações da solicitação e abas CEM PRO/CEM MOBI */}
                  <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Stack spacing={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                            NÚMERO DA SOLICITAÇÃO
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {String(detalhe.numeroSolicitacao).padStart(7, "0")}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                            DESCRIÇÃO DA SOLICITAÇÃO
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {detalhe.descricaoAtualizacao || "Não informado"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                            CEM Desktop
                          </Typography>
                          <Chip
                            label={situacaoLabels[detalhe.situacaoCemDelphi]}
                            color={situacaoChipColorMap[detalhe.situacaoCemDelphi]}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", fontWeight: 500, mb: 0.5, display: "block" }}>
                            CEM MOBI
                          </Typography>
                          <Chip
                            label={situacaoLabels[detalhe.situacaoCemMobi]}
                            color={situacaoChipColorMap[detalhe.situacaoCemMobi]}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </Grid>
                      </Grid>

                      {/* Abas CEM PRO / CEM MOBI */}
                      <Tabs
                        value={tipologiaTab}
                        onChange={(_, value) => setTipologiaTab(value)}
                        sx={{
                          borderBottom: 1,
                          borderColor: "divider",
                          "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            minHeight: 40,
                          },
                        }}
                      >
                        <Tab label="CEM Desktop" value="cemPro" />
                        <Tab label="CEM MOBI" value="cemMobi" />
                      </Tabs>

                      {/* Campo Vínculo(s) */}
                      {detalhe.vinculos.length > 0 && (
                        <FormControl fullWidth size="small">
                          <InputLabel>Vínculo(s)</InputLabel>
                          <Select
                            value={vinculoSelecionado}
                            onChange={(e) => setVinculoSelecionado(e.target.value as number | "")}
                            label="Vínculo(s)"
                          >
                            <MenuItem value="">
                              <em>Todos</em>
                            </MenuItem>
                            {detalhe.vinculos.map((vinculo) => (
                              <MenuItem key={vinculo.idSolicitacao} value={vinculo.idSolicitacao}>
                                {String(vinculo.numeroSolicitacao).padStart(7, "0")} - {situacaoLabels[vinculo.situacao]}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      {/* Botão DOCUMENTO TÉCNICO */}
                      <Stack direction="row" justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          startIcon={<ArticleIcon />}
                          onClick={downloadArquivoOriginal}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          DOCUMENTO TÉCNICO
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>

                  {/* Filtro de excluídas do pacote */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Switch
                        checked={mostrarApenasReprovadas}
                        onChange={(event) => setMostrarApenasReprovadas(event.target.checked)}
                        size="medium"
                      />
                      <Typography variant="body2" fontWeight={500}>
                        Visualizar somente excluídas do pacote
                      </Typography>
                    </Stack>
                  </Paper>

                  {/* Tabela de Tipologias */}
                  {tipologiasFiltradas.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        {mostrarApenasReprovadas
                          ? "Nenhuma tipologia foi excluída do pacote nesta solicitação."
                          : "Nenhuma tipologia vinculada a esta solicitação."}
                      </Typography>
                    </Paper>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Desenho</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Descrição</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Projetista</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Linha</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Data Atlz</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center">Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tipologiasFiltradas.map((item) => {
                            // foiAtualizada só se aplica quando não foi excluída
                            const foiAtualizada = !!item.numeroSolicitacaoDestino && !item.substituida;
                            
                            // Label e cor do status considerando exclusão do pacote
                            const statusLabel = item.situacao === 3
                              ? "Reprovada"
                              : item.descontinuado
                              ? "Descontinuada"
                              : itemSituacaoLabels[item.situacao] ?? "Ativo";
                            
                            const statusColor =
                              item.situacao === 3
                                ? "error"
                                : item.descontinuado
                                ? "warning"
                                : "default";

                            return (
                              <TableRow
                                key={item.idItem}
                                sx={{
                                  bgcolor: item.substituida
                                    ? alpha(theme.palette.error.main, 0.08)
                                    : item.situacao === 3
                                    ? alpha(theme.palette.error.main, 0.05)
                                    : item.descontinuado
                                    ? alpha(theme.palette.warning.main, 0.05)
                                    : foiAtualizada
                                    ? alpha(theme.palette.success.main, 0.05)
                                    : "transparent",
                                  "&:hover": {
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                  },
                                }}
                              >
                                <TableCell>
                                  <Avatar
                                    variant="rounded"
                                    sx={{
                                      width: 56,
                                      height: 56,
                                      bgcolor: "grey.200",
                                    }}
                                  >
                                    {item.codigo?.substring(0, 2) || "?"}
                                  </Avatar>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>
                                    {item.codigo}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Tooltip
                                    title={
                                      item.substituida
                                        ? "Tipologia excluída do pacote - não será incluída na homologação"
                                        : foiAtualizada
                                        ? `Tipologia atualizada na solicitação ${String(item.numeroSolicitacaoDestino).padStart(7, "0")}`
                                        : ""
                                    }
                                    arrow
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: foiAtualizada ? 600 : 400,
                                        color: item.substituida
                                          ? "text.secondary"
                                          : foiAtualizada
                                          ? "primary.main"
                                          : "text.primary",
                                        textDecoration: item.substituida ? "line-through" : "none",
                                      }}
                                    >
                                      {item.descricao}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{detalhe.projetistaNome}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{item.linha || "-"}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={statusLabel}
                                    color={statusColor}
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {item.dataVersao ? formatoData(item.dataVersao) : "-"}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Ver detalhes">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => {
                                        // Navegar para detalhes da tipologia se necessário
                                      }}
                                    >
                                      <SearchIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Stack>
              )}

              {detalheTab === "historico" && (
                <Paper variant="outlined" sx={{ p: 3 }}>
                  {detalhe.historicos.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                      Nenhum evento registrado para esta solicitação.
                    </Typography>
                  ) : (
                    <List sx={{ py: 0 }}>
                      {detalhe.historicos.map((historico, index) => (
                        <ListItem
                          key={`${historico.dataEvento}-${index}`}
                          alignItems="flex-start"
                          sx={{
                            pl: 0,
                            py: 2,
                            borderBottom: index < detalhe.historicos.length - 1 ? 1 : 0,
                            borderColor: "divider",
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                {historico.acao}
                              </Typography>
                            }
                            secondary={
                              <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                  {formatoData(historico.dataEvento)} · {historico.responsavel}
                                </Typography>
                                {historico.observacao && (
                                  <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
                                    {historico.observacao}
                                  </Typography>
                                )}
                              </Stack>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              )}

              {detalheTab === "anexos" && (
                <Stack spacing={2.5}>
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: "text.secondary" }}>
                      ADICIONAR ANEXO
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <LoadingButton
                        component="label"
                        variant="contained"
                        startIcon={<UploadFileIcon />}
                        loading={uploadingAnexo}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Adicionar Anexo
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip"
                          onChange={handleUploadAnexo}
                        />
                      </LoadingButton>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                        Formatos aceitos: PDF, DOC, XLS, imagens ou ZIP.
                      </Typography>
                    </Stack>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: "text.secondary" }}>
                      ANEXOS ({detalhe.anexos.length})
                    </Typography>
                    {detalhe.anexos.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                        Nenhum anexo complementar disponível.
                      </Typography>
                    ) : (
                      <List sx={{ py: 0 }}>
                        {detalhe.anexos.map((anexo, index) => (
                          <ListItem
                            key={anexo.idAnexo}
                            sx={{
                              py: 2,
                              borderBottom: index < detalhe.anexos.length - 1 ? 1 : 0,
                              borderColor: "divider",
                              alignItems: "flex-start",
                            }}
                            secondaryAction={
                              <IconButton edge="end" onClick={() => handleDownloadAnexo(anexo)} color="primary" sx={{ mt: 0.5 }}>
                                <DownloadIcon />
                              </IconButton>
                            }
                          >
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5, wordBreak: "break-word" }}>
                                  {anexo.nomeArquivo}
                                </Typography>
                              }
                              secondary={
                                <Stack spacing={0.5}>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatoData(anexo.dataUpload)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontSize: "0.7rem",
                                      wordBreak: "break-all",
                                      display: "block",
                                    }}
                                  >
                                    Hash: {anexo.hashArquivo}
                                  </Typography>
                                </Stack>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Paper>
                </Stack>
              )}
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Modal de confirmação emergencial */}
      <Dialog
        open={modalEmergencialOpen}
        onClose={() => {
          if (!processingEmergencial) {
            setModalEmergencialOpen(false);
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {acaoEmergencial ? "Confirmar atualização emergencial" : "Remover atualização emergencial"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {acaoEmergencial
              ? "Ao confirmar, todas as outras solicitações deste projetista serão congeladas e permanecerão apenas para consulta até a conclusão desta prioridade."
              : "Ao remover a marcação emergencial, as demais solicitações do projetista voltarão a ficar disponíveis para processamento."}
            {" "}Deseja continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalEmergencialOpen(false)} color="inherit" disabled={processingEmergencial}>
            Cancelar
          </Button>
          <LoadingButton
            onClick={confirmarEmergencial}
            color={acaoEmergencial ? "error" : "primary"}
            variant="contained"
            loading={processingEmergencial}
            autoFocus
          >
            Confirmar
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação de exclusão */}
      <Dialog
        open={modalDelete.isOpen}
        onClose={() => setModalDelete({ isOpen: false })}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Tem certeza que deseja excluir esta solicitação? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalDelete({ isOpen: false })} color="inherit">
            Cancelar
          </Button>
          <LoadingButton
            onClick={() => modalDelete.id && handleExcluirLinha(modalDelete.id)}
            color="error"
            variant="contained"
            loading={deletingLinha === modalDelete.id}
            autoFocus
          >
            Excluir
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Drawer de Filtro Avançado */}
      <DrawerFiltroAvancado
        open={drawerFiltroOpen}
        onClose={() => setDrawerFiltroOpen(false)}
        filtros={filtrosAvancados}
        onAplicar={handleAplicarFiltrosAvancados}
        projetistas={projetistas}
        solicitacoes={todasSolicitacoes}
        isLoading={isLoading}
      />

    </DashboardStackCustom>
  );
};
